import dayjs from "dayjs"
import { Groups, JobTitle, TeamMember } from "../../../../types"
import { TeamMemberStatus, TeamRoles } from "../types"
import sleep from "../../../../helpers/sleep"
import { GridApiCommunity } from "@mui/x-data-grid/models/api/gridApiCommunity"
import { MutableRefObject } from "react"
import { GridAutosizeOptions } from "@mui/x-data-grid"
import { User } from "../../../../redux/user"

export const getRole = (accessLevel: TeamRoles) => {
  const teamRoleKeys = Object.keys(TeamRoles)
  const index = Object.values(TeamRoles).indexOf(accessLevel)
  const role = teamRoleKeys[index]

  return role as keyof typeof TeamRoles
}
 
export const getRoleName = (accessLevel: TeamRoles) => {
  const role = getRole(accessLevel)

  return role === 'User' ? 'Team Member' : role
}

export const getFullName = (teamMember: TeamMember) => {
  return `${teamMember.firstname ?? ''} ${teamMember.lastname ?? ''}`.trim()
}

export const getGroups = (teamMember: TeamMember): Partial<Groups>[] => {
  const groups = teamMember.groups

  return groups?.length
    ? groups.filter(group => group.name).map(group => ({ name: group.name.name, group_id: group.group_id })) 
    : []
}

export const getStatus = (teamMember: TeamMember) => {
  return teamMember.account_active
    ? teamMember.verified
      ? TeamMemberStatus.Current
      : TeamMemberStatus.CurrentPending
    : TeamMemberStatus.Inactive
}

export const getPhoneNumber = (teamMember: TeamMember) => {
  const phoneNumber = teamMember.cellphone?.trim()

  return phoneNumber || 'Not Specified'
}

export const getLastLogin = (teamMember: TeamMember) => {
  const lastConnection = teamMember.last_connection_date
  
  return lastConnection
    ? dayjs(lastConnection).format('MM/DD/YYYY')
    : 'Never'
}

export const getFilteredTeamMembers = (search: string, teamMembers: TeamMember[]) => {
  if (search) {
    return structuredClone(teamMembers).filter(teamMember => {
      const cleanSearch = search.trim().toLowerCase()

      const fullName = getFullName(teamMember).toLowerCase()
      const email = teamMember.email?.toLowerCase() ?? ''
      const role = getRoleName(teamMember.access_level as TeamRoles).toLowerCase()
      const groups = getGroups(teamMember)
      const status = getStatus(teamMember).toLowerCase()
      const phoneNumber = getPhoneNumber(teamMember).toLowerCase()
      const position = teamMember.position?.toLowerCase() ?? ''
      
      return fullName.includes(cleanSearch) 
        || email.includes(cleanSearch)
        || role.includes(cleanSearch)
        || groups.some(group => group.name?.includes(cleanSearch))
        || status.includes(cleanSearch)
        || phoneNumber.includes(cleanSearch)
        || position.includes(cleanSearch)
    })
  } else return teamMembers
}

export const getJobTitle = (teamMember: TeamMember | User, jobTitles: JobTitle[]) => {
  const isPositionInJobTitles = jobTitles.find(job => {
      return job.job_title === teamMember.position
  })
    
  const title = teamMember.position
    ? isPositionInJobTitles
      ? teamMember.position.trim()
      : 'Other'
    : null

  const otherTitle = title === 'Other'
    ? teamMember.position?.trim()
    : null

  return { title, otherTitle }
}

export const validateName = (name: string) => {
  let isValid = false
  let message = ''

  const length = name.trim().length

  if (length === 0) {
    message = 'Name Required'
  } else if (length < 2) {
    message = 'Name Too Short'
  } else if (length > 50) {
    message = 'Name Too Long'
  } else {
    const regEx = /^(?=.*[a-zA-ZÀ-ÖØ-öø-ÿ])[a-zA-ZÀ-ÖØ-öø-ÿ'.\- 0-9]{2,}$/

    isValid = regEx.test(name)

    if (!isValid) message = 'Invalid Name'
  }

  return { isValid, message }
}

export const validateEmail = (email: string) => {
  let isValid = false
  let message = ''

  const length = email.trim().length

  if (length === 0) {
    message = 'Email Required'
  } else {
    const nameRegEx = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    isValid = nameRegEx.test(email)

    if (!isValid) message = 'Invalid Email'
  }

  return { isValid, message }
}

export const validateJobTitle = (jobTitle: string) => {
  let isValid = false
  let message = ''

  const length = jobTitle.trim().length

  if (length === 0) {
    message = 'Job Title Required'
  } else if (length > 255) {
    message = 'Job Title Too Long'
  } else {
    const regEx = /^(?=.*[a-zA-ZÀ-ÖØ-öø-ÿ])[a-zA-ZÀ-ÖØ-öø-ÿ'.\- 0-9]{1,}$/

    isValid = regEx.test(jobTitle)

    if (!isValid) message = 'Invalid Job Title'
  }

  return { isValid, message }  
}

export const autoSizeGrid = async (apiRef: MutableRefObject<GridApiCommunity>, autosizeOptions: GridAutosizeOptions) => {
  await sleep(5)
  apiRef.current?.autosizeColumns(autosizeOptions)
}
